import { clsx } from 'clsx';

import type { PortfolioMetricsDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

interface DrawdownGaugeProps {
  metrics: PortfolioMetricsDto | null | undefined;
  currency: string;
}

export function drawdownGaugeColor(drawdownPct: number): string {
  if (drawdownPct > 0.08) return '#EF4444';
  if (drawdownPct > 0.06) return '#EF4444';
  if (drawdownPct >= 0.04) return '#F59E0B';
  return '#10B981';
}

export function drawdownArcOffset(drawdownPct: number): number {
  const arcLength = 188;
  const fillRatio = Math.min(Math.max(drawdownPct / 0.08, 0), 1);
  return arcLength * (1 - fillRatio);
}

export function DrawdownGauge({ currency, metrics }: DrawdownGaugeProps) {
  const drawdownPct = metrics?.drawdownPct ?? 0;
  const color = drawdownGaugeColor(drawdownPct);
  return (
    <div className="rounded border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Drawdown</h2>
          <p className="text-sm text-text-muted">Trailing 90-day peak</p>
        </div>
        <span
          className={clsx(
            'rounded border px-2 py-1 text-sm font-semibold tabular-nums',
            drawdownPct > 0.08
              ? 'animate-pulse border-danger text-danger'
              : 'border-border text-text',
          )}
        >
          {(drawdownPct * 100).toFixed(1)}%
        </span>
      </div>
      <svg
        viewBox="0 0 220 126"
        role="img"
        aria-label={`Drawdown gauge showing ${(drawdownPct * 100).toFixed(1)} percent drawdown against the 8 percent hard block threshold.`}
        className="mt-4 w-full"
      >
        <path
          d="M 28 110 A 82 82 0 0 1 192 110"
          fill="none"
          stroke="#2A3142"
          strokeLinecap="round"
          strokeWidth="18"
        />
        <path
          d="M 28 110 A 82 82 0 0 1 192 110"
          fill="none"
          stroke={color}
          strokeDasharray="188"
          strokeDashoffset={drawdownArcOffset(drawdownPct)}
          strokeLinecap="round"
          strokeWidth="18"
        />
        <text x="28" y="124" fill="#A1A8B8" fontSize="12">
          0%
        </text>
        <text x="178" y="124" fill="#A1A8B8" fontSize="12">
          8%
        </text>
      </svg>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-text-muted">Peak NAV</div>
          <div className="font-semibold tabular-nums">
            {formatCurrency(metrics?.peakNav, currency)}
          </div>
        </div>
        <div>
          <div className="text-text-muted">Current NAV</div>
          <div className="font-semibold tabular-nums">{formatCurrency(metrics?.nav, currency)}</div>
        </div>
      </div>
    </div>
  );
}
